/**
 * PestFieldPreview — a LIVE render of the scout's real recording button for the
 * pest being configured (Implementation Spec §3.6, Desktop Spec §C row 6).
 *
 * Reuses the production recording buttons (IncidenceButton/LevelButton/
 * CountButton) so config and the field never drift — it is NOT a mock.
 *
 * Threshold-trip semantics: shows the button at the moment this pest trips its
 * flag — value = action_threshold, selected + over-threshold. Fully config-
 * derived; updates as fields change.
 *
 * Over-threshold rule (non-negotiable): red ! flag + red badge ONLY.
 * infestedHere is ALWAYS false → the red fill/outline never appears here, so
 * the two signals stay visually orthogonal.
 *
 * Inert: wrapped in pointer-events:none + aria-hidden — buttons reused
 * unmodified; the wrapper makes them non-interactive / non-tabbable.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IncidenceButton, LevelButton, CountButton } from './recording/PestButtons';
import type { SpecMeasurementType } from '../../scouting/pestConfig';
import './PestFieldPreview.css';

export interface PestFieldPreviewProps {
  code: string;
  name: string;
  measurementType: SpecMeasurementType;
  actionThreshold: number;
}

const noop = () => {};

export const PestFieldPreview: React.FC<PestFieldPreviewProps> = ({
  code, name, measurementType, actionThreshold,
}) => {
  const { t } = useTranslation();

  let button: React.ReactNode;
  if (measurementType === 'level') {
    const severity = Math.max(0, Math.min(4, Math.round(actionThreshold))) as 0 | 1 | 2 | 3 | 4;
    button = (
      <LevelButton code={code} name={name} severity={severity}
        active={actionThreshold > 0} overThreshold onTap={noop} onLongPress={noop} />
    );
  } else if (measurementType === 'count') {
    button = (
      <CountButton code={code} name={name} value={actionThreshold}
        active={actionThreshold > 0} overThreshold onTap={noop} onLongPress={noop} />
    );
  } else {
    button = (
      <IncidenceButton code={code} name={name} pct={actionThreshold}
        selected infestedHere={false} overThreshold onTap={noop} onLongPress={noop} />
    );
  }

  return (
    <div className="pest-field-preview">
      <div className="pest-field-preview__stage" aria-hidden="true">
        {button}
      </div>
      <span className="pest-field-preview__legend">{t('fieldScout.config.fieldPreviewLegend')}</span>
    </div>
  );
};

export default PestFieldPreview;
